<template>
  <div class="App">
    <div class="App__toolbar-box"></div>
    <ReloadSwipe />
    <div class="App__hdr">
      <HeaderMain />
    </div>
    <!-- <testplayer /> -->
    <div class="App__main">
      <router-view :key="route.fullPath"></router-view>
    </div>
    <div class="App__ftr">
      <FooterMain />
    </div>
    <CoinTooltip />
    <OverlayModule />
    <PonPopup v-if="isLessThanSM"/>
    <WidgetAuthorization />
    <transition name="Anim-Call-Widget-Search">
      <SearchWidget v-if="get_isShowSearch" />
    </transition>
    <transition name="Anim-Call-Auth-Widget">
      <ContentWidgetPopup v-if="get_isWidgetContentOpen" :key="route.fullPath" />
    </transition>
    <WidgetNavigation class="App__nav" v-if="isShowNavigation" />
    <transition name="Anim__opacity">
      <SliderViewer v-if="get_isShowSlider" />
    </transition>
    <transition name="Anim__dropdown">
      <ContentEditor v-if="get_isOpenEditor" />
    </transition>
    <transition name="Anim__dropdown">
      <ChannelsWidget v-if="get_isShowChannels" />
    </transition>
    <StickyAudioplayerPanel class="App__player" />
  </div>
</template>

<script setup>
  import { computed, ref, onMounted, onUnmounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { useRoute } from 'vue-router';
  import { useStore } from 'vuex';
  import { callOverlay } from '@/Groups/OverlayManager/public/utils/callOverlay';
  import { performLogout } from '@shared/utils/auth/logout';

  import DROPDOWN_NAMES from '@shared/constants/dropdowns/dropdownsName';
  import FooterMain from '@widgets/Footer/FooterMain.vue';
  import WidgetAuthorization from '@widgets/Authorization/WidgetAuthorization.vue';
  import SliderViewer from '@/Groups/Slider/public/modules/SliderViewer/SliderViewer.vue';
  import OverlayModule from '@/Groups/OverlayManager/public/modules/OverlayModule/OverlayModule.vue';
  import SearchWidget from '@widgets/SearchWidget/SearchWidget.vue';
  import ContentWidgetPopup from '@widgets/ContentWidget/ContentWidgetPopup.vue';
  import WidgetNavigation from '@widgets/NavigationWidget/WidgetNavigation.vue';
  import ContentEditor from '@widgets/ContentEditor/ContentEditor.vue';
  import ChannelsWidget from '@widgets/ChannelsWidget/ChannelsWidget.vue';
  import CoinTooltip from '@/Groups/CoinTooltip/public/modules/CoinTooltip/CoinTooltip.vue';
  import ReloadSwipe from '@features/ReloadSwipe/ReloadSwipe.vue';
  import HeaderMain from '@widgets/Header/HeaderMain.vue';
  import PonPopup from '@/Groups/Pon/public/PonPopup/PonPopup.vue';
  import StickyAudioplayerPanel from '@/Groups/Audioplayer/public/modules/StickyAudioplayerPanel/StickyAudioplayerPanel.vue';

  import { initAudioplayer } from '@/Groups/Audioplayer/public/utils/initAudioplayer';
  import { initScreenResize } from '@shared/composables/screenWidth/useScreenSize';
  import { useAutoUpdate } from '@shared/composables/updating/updateContent';
  import { useNotificationPoller } from '@/Groups/Notifications/public/composables/checkNewNotifications';
  import { initColorTheme } from '@/Groups/ColorThemesManager/public/utils/initColorTheme';
  import { useScreenSize } from '@/Groups/Audioplayer/private/imports/screenSize';
  //-----------------------
  const { startAutoUpdate } = useAutoUpdate();
  //-----------------------
  const { isLessThanSM } = useScreenSize();
  //-----------------------
  const { startPolling } = useNotificationPoller();
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const isShowNavigation = ref(false);
  const isShowAudioplayer = ref(false);

  const get_isModerator = computed(() => store.getters['UserSystem/hasModeratorPrivileges']);
  const get_isAuth = computed(() => store.state.UserSystem.user.isAuthenticated);
  const get_isShowSlider = computed(() => store.getters['Dropdowns/isInList'](DROPDOWN_NAMES.slider));
  const get_isShowSearch = computed(() => store.getters['Dropdowns/isInList'](DROPDOWN_NAMES.search));
  const get_isWidgetContentOpen = computed(() => store.getters['Dropdowns/isInList'](DROPDOWN_NAMES.contentPage));
  const get_isOpenEditor = computed(() => store.getters['Dropdowns/isInList'](DROPDOWN_NAMES.editor));
  const get_isShowChannels = computed(() => store.getters['Dropdowns/isInList'](DROPDOWN_NAMES.channels));

  onMounted(async () => {
    initColorTheme();
    initManifest();
    await initAuthorization();
    handleResize();
    addListeners();
    initAudioplayer();
    initScreenResize();
    startAutoUpdate();
    startPolling();
    /* if (get_isAuth.value) {
      subscribeUserToPush();
    } */
    fixNavPositionForIOS();
    window.addEventListener('resize', fixNavPositionForIOS);
    await checkRefreshToken();
  });



  onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('resize', fixNavPositionForIOS);
  });

  const addListeners = () => {
    window.addEventListener('resize', handleResize);
  };

  const handleResize = () => {
    isShowNavigation.value = window.innerWidth <= 1024;
    isShowAudioplayer.value = window.innerWidth > 1024;
  };

  const initAuthorization = async () => {
    const isToken = localStorage.getItem('access_token');
    let data = null;

    try {
      data = JSON.parse(isToken);
    } catch {
      await performLogout(store, router);
      return;
    }

    const accessToken = !!data?.accessToken;
    store.commit('UserSystem/toChangeAuthenticatedStatus', accessToken);

    if (accessToken) {
      try {
        await store.dispatch('UserSystem/toGetUserData');
      } catch {
        // Если токен уже был сброшен внутри refreshAccessToken (ошибка авторизации) —
        // очищаем состояние и редиректим. Если токен ещё есть — сетевая ошибка, не разлагиниваем.
        const tokenStillPresent = !!store.getters.getAccessToken;
        if (!tokenStillPresent) {
          store.commit('UserSystem/clearUserData');
          callOverlay({ text: 'Сессия закончилась, авторизуйтесь на сайте снова.', color: 'red', icon: 'person' });
          await router.push('/');
        }
      }
    }
  };

  const initManifest = () => {
    const BaseUrl = process.env.BASE_URL;
    const get_colorMode = computed(() => store.state.ColorThemeSystem.colorTheme);
    let manifest = document.querySelector('#manifest-capital');
    manifest.setAttribute('href', 'https://dropscapital.fra1.cdn.digitaloceanspaces.com/static/PWA/dev_manifest_light.json');
    /* manifest.setAttribute('href', `${BaseUrl}manifest/manifest_${get_colorMode.value}.json`); */
  };

  /* const initColorTheme = () => store.commit('ColorThemeSystem/initColorTheme'); */

  const checkRefreshToken = async () => {
    if (window.location.href.includes('localhost:8080') || window.location.href.includes('dropscapital.ngrok.dev')) return;

    if (sessionStorage.getItem('sessionExpiredMessage')) {
      callOverlay({ text: 'Сессия закончилась, авторизуйтесь на сайте снова.', color: 'red', icon: 'person' });
      sessionStorage.removeItem('sessionExpiredMessage');
    }

    // Если access token уже сброшен (logout произошёл внутри initAuthorization) — не дублируем
    if (!get_isAuth.value || !store.getters.getAccessToken) return;

    try {
      const response = await fetch('/api/token/refresh/check/', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.status === 401) {
        // Cookie отсутствует — пробуем полный refresh (другая вкладка могла обновить cookie)
        const refreshResponse = await fetch('/api/token/refresh/', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!refreshResponse.ok) {
          store.commit('UserSystem/clearUserData');
          await store.dispatch('logout');
          callOverlay({ text: 'Сессия закончилась, авторизуйтесь на сайте снова.', color: 'red', icon: 'person' });
        }
      }
    } catch (error) {
      console.error('Error checking refresh token:', error);
    }
  };

  // iOS Safari/Chrome bar hide/show workaround
  const fixNavPositionForIOS = () => {
    const nav = document.querySelector('.App__nav');
    if (!nav) return;
    const height = window.innerHeight;
    nav.style.bottom = '0px';
  };
</script>

<style lang="scss" scoped>
  @import './styles/_conf';
</style>
